import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import MfaEnrollment from '../components/MfaEnrollment';
import { Button } from '../components/ui/Button';
import { deleteAccount, disableMfa, updateProfile } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { usePlanLabel } from '../i18n/usePlanLabel';
import { appTheme, pitchTheme } from '../styles/theme';

const Page = styled.main`
  flex: 1;
  padding: 48px 24px 64px;
  background: ${appTheme.pageBg};
`;

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${appTheme.greenDark};
`;

const Intro = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: ${appTheme.textMuted};
`;

const Panel = styled.section`
  padding: 24px 20px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1.0625rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Avatar = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarInitial = styled.span`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${appTheme.green};
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
`;

const ProfileMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ProfileEmail = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${appTheme.text};
  word-break: break-all;
`;

const ProfileHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${appTheme.textMuted};
`;

const PlanBadge = styled.span`
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${appTheme.green};
  background: ${appTheme.mint};
  border-radius: ${appTheme.radiusPill};
  padding: 4px 10px;
  width: fit-content;
`;

const MethodList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MethodItem = styled.li`
  font-size: 0.875rem;
  color: ${appTheme.text};

  &::before {
    color: ${appTheme.green};
    font-weight: 700;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.text};
`;

const Input = styled.input`
  padding: 10px 14px;
  border-radius: ${appTheme.radiusMd};
  border: 1px solid ${appTheme.border};
  font-family: inherit;
  font-size: 0.875rem;
  color: ${appTheme.text};
  background: ${appTheme.pageBg};
  transition: border-color 0.15s ease, outline 0.15s ease;

  &:focus {
    outline: 2px solid ${appTheme.green};
    outline-offset: 1px;
    border-color: ${appTheme.green};
  }
`;

const PanelText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.textMuted};
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ModalOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(10, 46, 40, 0.4);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const ModalDialog = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 310;
  width: min(420px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  box-shadow: ${appTheme.shadowLg};
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? 1 : 0.96)});
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease, transform 0.2s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1.125rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.surface};
  color: ${appTheme.textMuted};
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${appTheme.surfaceHover};
    color: ${appTheme.text};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
`;

const ModalError = styled.p`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const DangerPanel = styled(Panel)`
  border-color: ${appTheme.dangerBorder};
  background: ${appTheme.dangerBg};
`;

const DangerText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.textMuted};
`;

const ConfirmBox = styled.div`
  padding: 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.surface};
  border: 1px solid ${appTheme.dangerBorder};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SuccessMessage = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.successBg};
  border: 1px solid #c5e8d4;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${appTheme.successText};
`;

const ErrorMessage = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const formatMemberSince = (isoDate: string): string =>
  new Date(isoDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, applyUser, logout } = useAuth();
  const userPlanLabel = usePlanLabel(user?.plan);

  const [name, setName] = useState(user?.name ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMfaEnroll, setShowMfaEnroll] = useState(false);
  const [showMfaDisable, setShowMfaDisable] = useState(false);
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [disablingMfa, setDisablingMfa] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (user) {
      setName(user.name);
    }
  }, [user?.name]);

  if (!user) {
    return null;
  }

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const openPasswordModal = () => {
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setShowPasswordModal(true);
  };

  const handleProfileSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingProfile(true);
    setError(null);
    setSuccessMessage(null);
    try {
      applyUser(await updateProfile({ name: name.trim() }));
      setSuccessMessage(t('profile.profileUpdated'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile.updateError'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setPasswordError(t('profile.passwordMismatch'));
      return;
    }

    setSavingPassword(true);
    setPasswordError(null);
    try {
      applyUser(
        await updateProfile({
          currentPassword: user.hasPassword ? currentPassword : undefined,
          newPassword,
        }),
      );
      closePasswordModal();
      setSuccessMessage(
        user.hasPassword ? t('profile.passwordUpdated') : t('profile.passwordSet'),
      );
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : t('profile.passwordUpdateError'));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDisableMfa = async (event: React.FormEvent) => {
    event.preventDefault();
    setDisablingMfa(true);
    setMfaError(null);
    try {
      applyUser(await disableMfa(mfaDisableCode.trim()));
      setShowMfaDisable(false);
      setMfaDisableCode('');
      setSuccessMessage(t('profile.mfaDisabledSuccess'));
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : t('profile.mfaDisableError'));
    } finally {
      setDisablingMfa(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await deleteAccount(
        user.hasPassword
          ? { password: deletePassword }
          : { confirmation: deleteConfirmation.trim() },
      );
      await logout();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile.deleteError'));
      setDeleting(false);
    }
  };

  return (
    <Page>
      <Container>
        <Header>
          <Title>{t('profile.title')}</Title>
          <Intro>{t('profile.intro')}</Intro>
        </Header>

        {successMessage && <SuccessMessage role="status">{successMessage}</SuccessMessage>}
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

        <Panel>
          <PanelTitle>{t('profile.account')}</PanelTitle>
          <ProfileRow>
            {user.avatarUrl ? (
              <Avatar src={user.avatarUrl} alt="" referrerPolicy="no-referrer" />
            ) : (
              <AvatarInitial>{user.name.charAt(0).toUpperCase()}</AvatarInitial>
            )}
            <ProfileMeta>
              <ProfileEmail>{user.email}</ProfileEmail>
              <PlanBadge>{userPlanLabel}</PlanBadge>
              <ProfileHint>
                {t('profile.memberSince', { date: formatMemberSince(user.createdAt) })}
              </ProfileHint>
            </ProfileMeta>
          </ProfileRow>
          <Form onSubmit={handleProfileSubmit}>
            <Label>
              {t('profile.displayName')}
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                minLength={2}
                required
              />
            </Label>
            <Button type="submit" $variant="primary" disabled={savingProfile || name.trim() === user.name}>
              {savingProfile ? t('profile.saving') : t('profile.saveName')}
            </Button>
          </Form>
        </Panel>

        <Panel>
          <PanelTitle>{t('profile.signInMethods')}</PanelTitle>
          <MethodList>
            <MethodItem>
              {user.hasPassword ? t('profile.emailPasswordSet') : t('profile.emailPasswordNotSet')}
            </MethodItem>
            <MethodItem>
              {user.hasGoogle ? t('profile.googleLinked') : t('profile.googleNotLinked')}
            </MethodItem>
          </MethodList>
        </Panel>

        <Panel>
          <PanelTitle>{t('profile.password')}</PanelTitle>
          <PanelText>
            {user.hasPassword ? t('profile.passwordUpdateHint') : t('profile.passwordAddHint')}
          </PanelText>
          <Button type="button" $variant="secondary" onClick={openPasswordModal}>
            {user.hasPassword ? t('profile.changePassword') : t('profile.setPassword')}
          </Button>
        </Panel>

        <Panel>
          <PanelTitle>{t('profile.mfa')}</PanelTitle>
          {!user.hasPassword && <PanelText>{t('profile.mfaNeedsPassword')}</PanelText>}

          {user.hasPassword && user.totpEnabled && (
            <>
              <PanelText>{t('profile.mfaEnabled')}</PanelText>
              {showMfaDisable ? (
                <ConfirmBox>
                  <Form onSubmit={handleDisableMfa}>
                    <Label>
                      {t('profile.mfaDisablePrompt')}
                      <Input
                        value={mfaDisableCode}
                        onChange={(event) =>
                          setMfaDisableCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                        }
                        placeholder="123456"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        minLength={6}
                        maxLength={6}
                      />
                    </Label>
                    {mfaError && <ModalError>{mfaError}</ModalError>}
                    <ActionRow>
                      <Button
                        type="submit"
                        $variant="danger"
                        disabled={disablingMfa || mfaDisableCode.length !== 6}
                      >
                        {disablingMfa ? t('profile.mfaDisabling') : t('profile.mfaDisable')}
                      </Button>
                      <Button
                        type="button"
                        $variant="secondary"
                        disabled={disablingMfa}
                        onClick={() => {
                          setShowMfaDisable(false);
                          setMfaDisableCode('');
                          setMfaError(null);
                        }}
                      >
                        {t('profile.mfaKeepOn')}
                      </Button>
                    </ActionRow>
                  </Form>
                </ConfirmBox>
              ) : (
                <Button type="button" $variant="secondary" onClick={() => setShowMfaDisable(true)}>
                  {t('profile.mfaDisableButton')}
                </Button>
              )}
            </>
          )}

          {user.hasPassword && !user.totpEnabled && (
            <>
              <PanelText>{t('profile.mfaEnrollHint')}</PanelText>
              {showMfaEnroll ? (
                <>
                  <MfaEnrollment
                    onEnabled={(updated) => {
                      applyUser(updated);
                      setShowMfaEnroll(false);
                      setSuccessMessage(t('profile.mfaEnabledSuccess'));
                    }}
                  />
                  <Button type="button" $variant="secondary" onClick={() => setShowMfaEnroll(false)}>
                    {t('common.cancel')}
                  </Button>
                </>
              ) : (
                <Button type="button" $variant="primary" onClick={() => setShowMfaEnroll(true)}>
                  {t('profile.mfaEnableButton')}
                </Button>
              )}
            </>
          )}
        </Panel>

        <DangerPanel>
          <PanelTitle>{t('profile.deleteAccount')}</PanelTitle>
          <DangerText>{t('profile.deleteWarning')}</DangerText>
          {showDeleteConfirm ? (
            <ConfirmBox>
              {user.hasPassword ? (
                <Label>
                  {t('profile.deletePasswordPrompt')}
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(event) => setDeletePassword(event.target.value)}
                    autoComplete="current-password"
                  />
                </Label>
              ) : (
                <Label>
                  {t('profile.deleteEmailPrompt')}
                  <Input
                    type="email"
                    value={deleteConfirmation}
                    onChange={(event) => setDeleteConfirmation(event.target.value)}
                    placeholder={user.email}
                    autoComplete="off"
                  />
                </Label>
              )}
              <ActionRow>
                <Button
                  type="button"
                  $variant="danger"
                  disabled={
                    deleting ||
                    (user.hasPassword
                      ? !deletePassword
                      : deleteConfirmation.trim().toLowerCase() !== user.email)
                  }
                  onClick={handleDeleteAccount}
                >
                  {deleting ? t('profile.deleting') : t('profile.deleteConfirm')}
                </Button>
                <Button type="button" $variant="secondary" disabled={deleting} onClick={() => setShowDeleteConfirm(false)}>
                  {t('common.cancel')}
                </Button>
              </ActionRow>
            </ConfirmBox>
          ) : (
            <Button type="button" $variant="danger" onClick={() => setShowDeleteConfirm(true)}>
              {t('profile.deleteAccount')}
            </Button>
          )}
        </DangerPanel>
      </Container>

      <ModalOverlay $open={showPasswordModal} onClick={closePasswordModal} aria-hidden={!showPasswordModal} />
      <ModalDialog
        $open={showPasswordModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="password-modal-title">
            {user.hasPassword ? t('profile.changePassword') : t('profile.setPassword')}
          </ModalTitle>
          <CloseButton type="button" aria-label={t('common.close')} onClick={closePasswordModal}>
            ×
          </CloseButton>
        </ModalHeader>

        {passwordError && <ModalError>{passwordError}</ModalError>}

        <Form onSubmit={handlePasswordSubmit}>
          {user.hasPassword && (
            <Label>
              {t('profile.currentPassword')}
              <Input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </Label>
          )}
          <Label>
            {t('profile.newPassword')}
            <Input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </Label>
          <Label>
            {t('profile.confirmNewPassword')}
            <Input
              type="password"
              value={confirmNewPassword}
              onChange={(event) => setConfirmNewPassword(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </Label>
          <ModalActions>
            <Button type="submit" $variant="primary" disabled={savingPassword}>
              {savingPassword
                ? t('profile.saving')
                : user.hasPassword
                  ? t('profile.updatePassword')
                  : t('profile.setPassword')}
            </Button>
            <Button type="button" $variant="secondary" disabled={savingPassword} onClick={closePasswordModal}>
              {t('common.cancel')}
            </Button>
          </ModalActions>
        </Form>
      </ModalDialog>
    </Page>
  );
};

export default ProfilePage;
